import { useEffect, useMemo } from 'react'
import { Switch } from '@/components/ui/switch'

// Mirrors packages/widget/src/types/animations.ts. Kept inline here so the
// dashboard doesn't need a cross-package import path.
export type AnimationPresetId =
  | 'pulse-ring'
  | 'aurora-gradient'
  | 'breathing-orb'
  | 'voice-orb'

export interface CommonPresetParams {
  colorMode?: 'brand' | 'custom'
  color?: string
  intensity?: number
  speed?: number
  hueRange?: number
  blur?: number
  glowSize?: number
  breathDuration?: number
  reactivity?: number
  logoTreatment?: 'circle' | 'square' | 'silhouette'
  logoScale?: number
}

export interface WidgetAnimationsConfig {
  presetId: AnimationPresetId
  voiceReactiveEnabled?: boolean
  reducedMotionFallback?: AnimationPresetId
  params?: Partial<Record<AnimationPresetId, CommonPresetParams>>
}

const DEFAULT: WidgetAnimationsConfig = {
  presetId: 'pulse-ring',
  voiceReactiveEnabled: true,
  reducedMotionFallback: 'pulse-ring',
  params: {
    'pulse-ring': { colorMode: 'brand', intensity: 4, speed: 1 },
    'aurora-gradient': { colorMode: 'brand', intensity: 6, speed: 1, hueRange: 60, blur: 12 },
    'breathing-orb': { colorMode: 'brand', intensity: 5, speed: 1, glowSize: 0.7, breathDuration: 3.6 },
    'voice-orb': { colorMode: 'brand', intensity: 6, speed: 1, reactivity: 7, logoTreatment: 'circle', logoScale: 0.78 },
  },
}

const PRESETS: Array<{ id: AnimationPresetId; label: string; description: string }> = [
  { id: 'pulse-ring', label: 'Pulse Ring', description: 'Soft halo around the launcher. Lightweight, professional.' },
  { id: 'aurora-gradient', label: 'Aurora', description: 'Slow conic-gradient sweep. Premium, eye-catching.' },
  { id: 'breathing-orb', label: 'Breathing Orb', description: 'Calm radial glow that breathes. Inviting.' },
  { id: 'voice-orb', label: 'Voice Orb', description: 'Logo-centered orb that morphs with voice. In-call hero.' },
]

interface Props {
  webVoiceEnabled: boolean
  onWebVoiceChange: (next: boolean) => void
  animations: WidgetAnimationsConfig | null
  onAnimationsChange: (next: WidgetAnimationsConfig) => void
  primaryColor: string
}

export default function WidgetAnimationsSection({
  webVoiceEnabled,
  onWebVoiceChange,
  animations,
  onAnimationsChange,
  primaryColor,
}: Props) {
  // Initialize with defaults the first time this section renders so saving
  // creates a complete record even if the user only flipped one knob.
  useEffect(() => {
    if (!animations) onAnimationsChange(DEFAULT)
  }, [])

  const cfg = animations || DEFAULT
  const activePreset = cfg.presetId
  const activeParams = (cfg.params?.[activePreset] || {}) as CommonPresetParams

  function updatePreset(next: AnimationPresetId) {
    onAnimationsChange({ ...cfg, presetId: next })
  }

  function updateParam<K extends keyof CommonPresetParams>(key: K, value: CommonPresetParams[K]) {
    const nextParams = {
      ...(cfg.params || {}),
      [activePreset]: { ...activeParams, [key]: value },
    }
    onAnimationsChange({ ...cfg, params: nextParams })
  }

  function updateVoiceReactive(next: boolean) {
    onAnimationsChange({ ...cfg, voiceReactiveEnabled: next })
  }

  // Build a CSS color preview based on params + brand color so the swatches
  // look like the actual production tint.
  const previewColor = useMemo(() => {
    if (activeParams.colorMode === 'custom' && activeParams.color) return activeParams.color
    return primaryColor
  }, [activeParams.colorMode, activeParams.color, primaryColor])

  return (
    <div className="border-t border-border pt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-md font-semibold text-foreground">Voice & Animations</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Enable in-browser voice calls and pick the launcher's animation style.
          </p>
        </div>
      </div>

      {/* Web voice toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/40 mb-6">
        <div>
          <div className="text-sm font-medium text-foreground">Browser voice calls</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Visitors can talk to your AI directly from the chat widget — no phone needed.
          </p>
        </div>
        <Switch checked={webVoiceEnabled} onCheckedChange={onWebVoiceChange} />
      </div>

      {/* Preset gallery */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => updatePreset(p.id)}
            className={`text-left rounded-lg border p-3 transition ${
              activePreset === p.id
                ? 'border-brand-primary ring-2 ring-brand-primary/20 bg-brand-primary/5'
                : 'border-border hover:border-brand-primary/50'
            }`}
          >
            <PresetThumbnail preset={p.id} color={previewColor} />
            <div className="mt-2 text-sm font-medium text-foreground">{p.label}</div>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{p.description}</p>
          </button>
        ))}
      </div>

      {/* Per-preset controls */}
      <div className="space-y-4">
        <Slider
          label="Intensity"
          value={activeParams.intensity ?? 5}
          min={1}
          max={10}
          step={1}
          onChange={(v) => updateParam('intensity', v)}
        />
        <Slider
          label="Speed"
          value={activeParams.speed ?? 1}
          min={0.5}
          max={2}
          step={0.1}
          format={(v) => `${v.toFixed(1)}x`}
          onChange={(v) => updateParam('speed', v)}
        />
        <ColorMode params={activeParams} onChange={(updates) => {
          for (const [k, v] of Object.entries(updates)) {
            updateParam(k as keyof CommonPresetParams, v as any)
          }
        }} />

        {activePreset === 'aurora-gradient' && (
          <>
            <Slider label="Hue range" value={activeParams.hueRange ?? 60} min={0} max={180} step={5}
              format={(v) => `${v}°`} onChange={(v) => updateParam('hueRange', v)} />
            <Slider label="Blur" value={activeParams.blur ?? 12} min={0} max={32} step={1}
              format={(v) => `${v}px`} onChange={(v) => updateParam('blur', v)} />
          </>
        )}

        {activePreset === 'breathing-orb' && (
          <>
            <Slider label="Glow size" value={activeParams.glowSize ?? 0.7} min={0.3} max={1.2} step={0.05}
              format={(v) => v.toFixed(2)} onChange={(v) => updateParam('glowSize', v)} />
            <Slider label="Breath duration" value={activeParams.breathDuration ?? 3.6} min={1.5} max={6} step={0.2}
              format={(v) => `${v.toFixed(1)}s`} onChange={(v) => updateParam('breathDuration', v)} />
          </>
        )}

        {activePreset === 'voice-orb' && (
          <>
            <Slider label="Voice reactivity" value={activeParams.reactivity ?? 7} min={1} max={10} step={1}
              onChange={(v) => updateParam('reactivity', v)} />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Logo treatment</label>
              <select
                value={activeParams.logoTreatment || 'circle'}
                onChange={(e) => updateParam('logoTreatment', e.target.value as any)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="circle">Circle crop (best for square logos)</option>
                <option value="square">White card (rectangular logos)</option>
                <option value="silhouette">Silhouette (mono logos)</option>
              </select>
            </div>
            <Slider label="Logo size" value={activeParams.logoScale ?? 0.78} min={0.5} max={1} step={0.02}
              format={(v) => `${Math.round(v * 100)}%`} onChange={(v) => updateParam('logoScale', v)} />
          </>
        )}

        <div className="flex items-center justify-between p-3 rounded-lg border border-border">
          <div>
            <div className="text-sm font-medium text-foreground">Voice-reactive animations</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Orb and ambient effects react to mic & agent audio levels during a call.
            </p>
          </div>
          <Switch
            checked={cfg.voiceReactiveEnabled !== false}
            onCheckedChange={updateVoiceReactive}
          />
        </div>
      </div>
    </div>
  )
}

// ---------- Helpers ---------------------------------------------------------

function Slider({ label, value, min, max, step, format, onChange }: {
  label: string
  value: number
  min: number
  max: number
  step: number
  format?: (v: number) => string
  onChange: (next: number) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-xs text-muted-foreground tabular-nums">
          {format ? format(value) : value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-brand-primary"
      />
    </div>
  )
}

function ColorMode({ params, onChange }: {
  params: CommonPresetParams
  onChange: (updates: Partial<CommonPresetParams>) => void
}) {
  const isCustom = params.colorMode === 'custom'
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">Color</label>
      <div className="flex items-center gap-2">
        <select
          value={params.colorMode || 'brand'}
          onChange={(e) => onChange({ colorMode: e.target.value as 'brand' | 'custom' })}
          className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
        >
          <option value="brand">Brand color</option>
          <option value="custom">Custom</option>
        </select>
        {isCustom && (
          <>
            <input
              type="color"
              value={params.color || '#C73E2D'}
              onChange={(e) => onChange({ color: e.target.value })}
              className="h-9 w-16 rounded border border-border"
            />
            <input
              type="text"
              value={params.color || '#C73E2D'}
              onChange={(e) => onChange({ color: e.target.value })}
              className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
              placeholder="#C73E2D"
            />
          </>
        )}
      </div>
    </div>
  )
}

function PresetThumbnail({ preset, color }: { preset: AnimationPresetId; color: string }) {
  // Static SVG/CSS thumbnails — fast to render, no audio context needed.
  // Each cap thumbnail is 84×60.
  if (preset === 'pulse-ring') {
    return (
      <div className="relative h-14 rounded-md overflow-hidden bg-gradient-to-br from-muted/40 to-muted">
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ ['--c' as any]: color }}
        >
          <span
            className="block w-7 h-7 rounded-full"
            style={{ background: color, boxShadow: `0 0 0 4px ${color}33, 0 0 0 8px ${color}11` }}
          />
        </div>
      </div>
    )
  }
  if (preset === 'aurora-gradient') {
    return (
      <div
        className="h-14 rounded-md"
        style={{
          background: `conic-gradient(from 0deg, ${color}, ${color}66, ${color})`,
          filter: 'blur(2px)',
        }}
      />
    )
  }
  if (preset === 'breathing-orb') {
    return (
      <div className="h-14 rounded-md bg-muted/40 flex items-center justify-center">
        <span
          className="block w-10 h-10 rounded-full"
          style={{ background: `radial-gradient(circle, ${color} 0%, ${color}22 70%, transparent 100%)` }}
        />
      </div>
    )
  }
  // voice-orb
  return (
    <div className="h-14 rounded-md bg-muted/40 flex items-center justify-center relative">
      <span
        className="absolute block w-12 h-12 rounded-full"
        style={{ background: `${color}22`, animation: 'none' }}
      />
      <span
        className="relative block w-7 h-7 rounded-full"
        style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
      />
    </div>
  )
}
